import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    } else {
      setStatus('failed');
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    
    try {
      const response = await axios.get(`${API}/api/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setPaymentData(response.data);
      } else if (response.data.status === 'expired' || attempts >= maxAttempts) {
        setStatus('failed');
      } else {
        // Keep polling
        setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
      if (attempts >= maxAttempts) {
        setStatus('failed');
      } else {
        setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), 2000);
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount || 0);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="payment-success-page">
      <Card className="w-full max-w-md bg-card border-border">
        <CardContent className="p-8 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="h-16 w-16 mx-auto mb-4 text-primary animate-spin" />
              <h2 className="text-xl font-bold mb-2">Processing Payment</h2>
              <p className="text-muted-foreground">Please wait while we verify your payment...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="bg-green-500/20 rounded-full p-4 w-fit mx-auto mb-4">
                <CheckCircle className="h-16 w-16 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-green-500">Payment Successful!</h2>
              <p className="text-muted-foreground mb-4">
                Thank you for your payment of {formatCurrency(paymentData?.amount)}
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                A confirmation email will be sent to your registered email address.
              </p>
              <Button onClick={() => navigate('/client')} className="w-full">
                Return to Dashboard
              </Button>
            </>
          )}

          {status === 'failed' && (
            <>
              <div className="bg-red-500/20 rounded-full p-4 w-fit mx-auto mb-4">
                <XCircle className="h-16 w-16 text-red-500" />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-red-500">Payment Failed</h2>
              <p className="text-muted-foreground mb-6">
                We couldn't process your payment. Please try again or contact support.
              </p>
              <div className="space-y-2">
                <Button onClick={() => navigate('/client')} className="w-full">
                  Return to Dashboard
                </Button>
                <Button variant="outline" onClick={() => window.location.reload()} className="w-full">
                  Try Again
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
